import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";

const HERO_IMAGE = "/images/ecoption-hero-wide.webp";
const WHATSAPP_URL = `https://wa.me/351920547223?text=${encodeURIComponent(
  "Olá, gostaria de obter mais informações sobre os vossos produtos."
)}`;

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] } }),
};

export default function Hero() {
  const scrollTo = (id) => document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="inicio" className="relative pt-24 lg:pt-24 pb-20 lg:pb-24 overflow-hidden bg-background" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid-soft pointer-events-none opacity-50" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#00AEEF]/10 via-[#EC008C]/8 to-[#FF7F00]/10 blur-3xl pointer-events-none" />

      <div className="relative w-full px-5 lg:px-8 flex flex-col items-center text-center">
        {/* Hero showcase image */}
        <div className="relative w-full">
          <div className="relative rounded-2xl overflow-hidden border border-border shadow-[0_40px_100px_-30px_rgba(10,31,68,0.35)] bg-[#010911] flex items-center justify-center">
            <img
              src={HERO_IMAGE}
              alt="Ecoption Supply — Consumíveis de impressão · Qualidade que impulsiona o seu negócio"
              className="block w-auto max-w-full h-auto max-h-[calc(100svh-7.5rem)] object-contain"
              loading="eager"
              fetchPriority="high"
              decoding="async"
              width="1672"
              height="941"
            />
            <div className="absolute inset-x-0 bottom-0 h-1 cmyk-gradient" />
          </div>

          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full cmyk-gradient blur-2xl opacity-50 -z-10" />
        </div>



        {/* Headline + CTAs (complementa a imagem, não repete) */}
        <motion.h1
          variants={fadeUp} initial="hidden" animate="show" custom={2}
          className="mt-16 md:mt-20 font-display text-3xl sm:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-foreground max-w-3xl"
          data-testid="hero-title"
        >
          Tinteiros, toners e fornecimento empresarial e doméstico{" "}
          <span className="cmyk-text">com a confiança de duas décadas</span>.
        </motion.h1>

        <motion.p
          variants={fadeUp} initial="hidden" animate="show" custom={3}
          className="mt-6 text-base lg:text-lg text-muted-foreground max-w-2xl leading-relaxed"
          data-testid="hero-subtitle"
        >
          Consumíveis de impressão, papel, rolos térmicos, reparação de
          impressoras e economato — entrega rápida e preços competitivos para
          empresas e instituições.
        </motion.p>

        <motion.div
          variants={fadeUp} initial="hidden" animate="show" custom={4}
          className="mt-9 flex flex-wrap items-center justify-center gap-3"
        >
          <Button
            onClick={() => scrollTo("#contactos")}
            className="group bg-[#0A1F44] hover:bg-[#112E5C] dark:bg-white dark:hover:bg-slate-200 dark:text-[#0A1F44] text-white rounded-full px-7 h-12 text-sm font-semibold shadow-[0_18px_40px_-12px_rgba(10,31,68,0.55)]"
            data-testid="hero-cta-primary"
          >
            Pedir Orçamento
            <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
          </Button>
          <Button
            variant="outline"
            asChild
            className="rounded-full px-7 h-12 text-sm font-semibold border-border text-foreground hover:bg-muted"
            data-testid="hero-cta-secondary"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" aria-label="Falar com a Ecoption Supply pelo WhatsApp">
              <Phone size={16} className="mr-2" /> Falar Connosco
            </a>
          </Button>
        </motion.div>

      </div>
    </section>
  );
}
